import { QueryClient, QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { Accomplishment } from "../models/Accomplishment";
import { AccomplishmentType } from "../models/AccomplishmentType";
import AccomplishmentService from "../services/AccomplishmentService";
import AssignationService from "../services/AssignationService";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, OnDataFetch, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";

export const accomplishmentKeys = new QueryKeyFactory(AccomplishmentService.path);
export const userKeys = new QueryKeyFactory(UserService.path);
const assignationKeys = new QueryKeyFactory("/ensolvers-ops/assignation");
const NO_TYPES: AccomplishmentType[] = [];
const NO_ASSIGNATIONS: AutoCompleteUIDTO[] = [];
const ANONYMOUS_CORE_USER_ID = "anonymous";

function isCachedListFresh(state: { status: string; isInvalidated: boolean; dataUpdatedAt: number } | undefined): boolean {
  if (!state || state.status !== "success" || state.isInvalidated) {
    return false;
  }
  return Date.now() - state.dataUpdatedAt < TABLE_QUERY_STALE_TIME;
}

function trackListLoading<T>(
  fetchPage: OnDataFetch<T>,
  queryKey: (page: number, pageSize: number, sortBy?: string, sortDir?: string) => QueryKey,
  queryClient: QueryClient,
  pendingLoads: { current: number },
  setIsListLoading: (loading: boolean) => void,
): OnDataFetch<T> {
  return async (page, pageSize, sortBy, sortDir) => {
    const fresh = isCachedListFresh(queryClient.getQueryState(queryKey(page, pageSize, sortBy, sortDir)));
    if (!fresh) {
      pendingLoads.current += 1;
      setIsListLoading(true);
    }
    try {
      return await fetchPage(page, pageSize, sortBy, sortDir);
    } finally {
      if (!fresh) {
        pendingLoads.current = Math.max(0, pendingLoads.current - 1);
      }
      setIsListLoading(pendingLoads.current > 0);
    }
  };
}

enum AccomplishmentStatus {
  ALL = "ALL",
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
}

const useAccomplishment = ({ loadFormData = false }: { loadFormData?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>(AccomplishmentStatus.ALL);
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [isListLoading, setIsListLoading] = useState(true);
  const pendingListLoads = useRef(0);
  const coreUserId = AuthService.getUser()?.externalId;

  const currentUserQuery = useQuery({
    queryKey: userKeys.detail(`core:${coreUserId ?? ANONYMOUS_CORE_USER_ID}`),
    queryFn: () => unwrap(UserService.findByCoreUserId(coreUserId!)),
    enabled: !!coreUserId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const getAllPaginated = useMemo(() => {
    const queryKey = (page: number, pageSize: number, sortBy?: string, sortDir?: string) =>
      accomplishmentKeys.list({ page, pageSize, sortBy, sortDir, status: statusFilter, scope: "approval" });
    return trackListLoading(
      createTableQuery<Accomplishment>({
        queryKey,
        queryFn: (page, pageSize) => unwrap(AccomplishmentService.getAllAccomplishmentsByStatus(page, pageSize, statusFilter)),
        client: queryClient,
      }),
      queryKey,
      queryClient,
      pendingListLoads,
      setIsListLoading,
    );
  }, [queryClient, statusFilter]);

  const refresh = () => refreshTableQuery(accomplishmentKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const statusMutation = useMutation({
    mutationFn: ({ externalId, status }: { externalId: string; status: "Approved" | "Pending" | "Rejected" }) =>
      unwrap(AccomplishmentService.updateStatus(externalId, status)),
    onSuccess: async () => {
      await refresh();
      NotificationService.info("Accomplishment status changed successfully");
    },
  });

  const handleUpdateStatus = (instance: Accomplishment, status: "Approved" | "Pending" | "Rejected") => {
    if (!instance.externalId) return;
    statusMutation.mutate({ externalId: instance.externalId, status });
  };

  const typesQuery = useQuery({
    queryKey: accomplishmentKeys.detail("types"),
    queryFn: () => unwrap(AccomplishmentService.getTypes()),
    enabled: loadFormData,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const assignationsQuery = useQuery({
    queryKey: assignationKeys.list({ scope: "form-names" }),
    queryFn: () => unwrap(AssignationService.findAssignationsNamesByUser(undefined, null)),
    enabled: loadFormData,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const accomplishmentTypes = typesQuery.data ?? NO_TYPES;

  const getAccomplishmentData = (externalId: string) => accomplishmentTypes.find((type) => type.externalId === externalId);

  return {
    isLoading: isListLoading,
    statusFilter,
    setStatusFilter,
    getAllPaginated,
    handleUpdateStatus,
    forceUpdateOn,
    userExternalId: currentUserQuery.data?.externalId,
    accomplishmentTypes,
    userAssignations: assignationsQuery.data ?? NO_ASSIGNATIONS,
    getAccomplishmentData,
  };
};

export const useMyAccomplishments = () => {
  const queryClient = useQueryClient();
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [isListLoading, setIsListLoading] = useState(true);
  const pendingListLoads = useRef(0);

  const pointsQuery = useQuery({
    queryKey: accomplishmentKeys.detail("monthly-points"),
    queryFn: () => unwrap(AccomplishmentService.getMonthlyTotalPoints()),
  });

  const onDataFetch = useMemo(() => {
    const queryKey = (page: number, pageSize: number, sortBy?: string, sortDir?: string) =>
      accomplishmentKeys.list({ page, pageSize, sortBy, sortDir, scope: "mine" });
    return trackListLoading(
      createTableQuery<Accomplishment>({
        queryKey,
        queryFn: (page, pageSize) => unwrap(AccomplishmentService.getMyAccomplishments(page, pageSize)),
        client: queryClient,
      }),
      queryKey,
      queryClient,
      pendingListLoads,
      setIsListLoading,
    );
  }, [queryClient]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: accomplishmentKeys.all() });
    setForceUpdateOn(Date.now());
  };

  const createMutation = useMutation({
    mutationFn: ({ reporterId, accomplishment }: { reporterId: string; accomplishment: Accomplishment }) =>
      unwrap(AccomplishmentService.createAccomplishment(reporterId, accomplishment)),
    onSuccess: async () => {
      await refresh();
      NotificationService.info("Accomplishment created successfully");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (accomplishment: Accomplishment) =>
      unwrap(AccomplishmentService.updateAccomplishment(accomplishment.externalId!, accomplishment)),
    onSuccess: async () => {
      await refresh();
      NotificationService.info("Accomplishment updated successfully");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (externalId: string) => unwrap(AccomplishmentService.deleteAccomplishment(externalId)),
    onSuccess: async () => {
      await refresh();
      NotificationService.info("Accomplishment deleted successfully");
    },
  });

  const handleOnCreate = (accomplishment: Accomplishment) => {
    const reporterId = AuthService.getUser()?.externalId;
    if (!reporterId) return Promise.resolve();
    return createMutation.mutateAsync({ reporterId, accomplishment });
  };

  const handleOnUpdate = (accomplishment: Accomplishment) => {
    return updateMutation.mutateAsync(accomplishment);
  };

  const handleOnDelete = (externalId?: string) => {
    if (!externalId) return;
    deleteMutation.mutate(externalId);
  };

  return {
    onDataFetch,
    forceUpdateOn,
    totalPoints: pointsQuery.data ?? 0,
    isListLoading,
    isLoading: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    handleOnCreate,
    handleOnUpdate,
    handleOnDelete,
  };
};

export default useAccomplishment;
